import { SportsSoccer } from '@mui/icons-material';
import { Box, TableRow } from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonTableApp,
  TableCellApp,
  TableCellCategoria,
  TableModuleApp,
  TypographyApp,
  TypographyTableApp,
} from '../../components/custom/CustomComponents';
import { Context } from '../../service/Context';
import { capitalizar, ordenarLista } from '../../utils/UtilesComun';
import { obtenerCategoriaCodigo, obtenerEstadoJugadorEnCategoria } from '../../utils/UtilesJugador';
import BotonEdadCheck from '../botones/BotonEdadCheck';
import BotonListasConJugador from '../botones/BotonListasConJugador';
import BotonObservacionesLista from '../botones/BotonObservacionesLista';

/**
 * Columnas de la tabla de jugadores de una liga.
 */
const columns = [
  { field: 'nombreJugador', label: 'Nombre', width: '30%', tooltip: 'Nombre del jugador' },
  { field: 'nombreEquipo', label: 'Equipo', width: '32%', tooltip: 'Equipo' },
  {
    field: 'seguimiento',
    label: 'Seg.',
    width: '8%',
    align: 'center',
    sortable: false,
    tooltip: 'Añadir a listas de scouting o registrar observaciones',
    sx: { textAlign: 'center', px: 0.5 },
  },
  {
    field: 'categoria',
    label: 'Cat',
    width: '5%',
    align: 'center',
    tooltip: 'Categoría',
    sx: { textAlign: 'center' },
  },
  {
    field: 'convocatorias',
    label: 'PC',
    width: '5%',
    align: 'center',
    tooltip: 'Partidos Convocado',
    sx: { textAlign: 'center' },
  },
  {
    field: 'titularidades',
    label: 'PT',
    width: '5%',
    align: 'center',
    tooltip: 'Partidos Titular',
    sx: { textAlign: 'center' },
  },
  {
    field: 'goles',
    label: 'Goles',
    width: '5%',
    align: 'center',
    tooltip: 'Goles marcados',
    icon: SportsSoccer,
    sx: { borderLeft: '1px solid #e2e8f0', textAlign: 'center' },
  },
  {
    field: 'ano',
    label: 'Año',
    width: '10%',
    align: 'center',
    tooltip: 'Año de nacimiento y verificación de edad',
    sx: { borderLeft: '1px solid #e2e8f0', textAlign: 'center' },
  },
];

/**
 * Fila de la tabla de jugadores de una liga.
 */
const FilaJugadorLiga = ({ datosLiga, jugadorLiga, columns, listasJugador, handleListasChange, onUpdate }) => {
  const navigate = useNavigate();

  const anoTemporada = parseInt((datosLiga?.temporada || jugadorLiga?.temporada || '').split('-')[1], 10);
  const categoriaJugador = obtenerCategoriaCodigo(anoTemporada, jugadorLiga.ano);
  const categoriaEquipo = datosLiga.categoria;
  const estadoCategoriaJugador = obtenerEstadoJugadorEnCategoria(categoriaJugador, categoriaEquipo);
  const enLista = Boolean(listasJugador && listasJugador.length > 0);

  return (
    <TableRow
      key={jugadorLiga.idJugador}
      hover
      sx={{
        transition: 'background-color 0.15s ease-in-out',
        '&:hover': {
          backgroundColor: '#e8edf2 !important',
        },
      }}
    >
      {columns.map((col) => {
        if (col.field === 'seguimiento') {
          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.5,
                px: 0.25,
                textAlign: 'center',
                width: col.width,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <BotonListasConJugador
                  idJugador={jugadorLiga.idJugador}
                  enLista={true}
                  badge={true}
                  compact={true}
                  listasConJugador={listasJugador}
                  onListasChange={handleListasChange}
                />
                <BotonObservacionesLista
                  idJugador={jugadorLiga.idJugador}
                  observaciones={jugadorLiga.observaciones}
                  badge={true}
                  compact={true}
                  onUpdate={onUpdate}
                />
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'nombreJugador') {
          return (
            <TypographyTableApp key={col.field}>
              <ButtonTableApp
                enLista={enLista}
                onClick={() => navigate(`/jugador/${jugadorLiga.idJugador}`)}
                text={capitalizar(jugadorLiga.nombreJugador)}
              />
            </TypographyTableApp>
          );
        }

        if (col.field === 'nombreEquipo') {
          return (
            <TypographyTableApp key={col.field}>
              <ButtonTableApp
                onClick={() => navigate(`/equipo/${jugadorLiga.idEquipo}`)}
                text={capitalizar(jugadorLiga.nombreEquipo)}
              />
            </TypographyTableApp>
          );
        }

        if (col.field === 'ano') {
          return (
            <TableCellApp
              key={col.field}
              sx={{
                borderLeft: '1px solid #e2e8f0',
                py: 0.5,
                px: 0.5,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <TypographyApp sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{jugadorLiga.ano}</TypographyApp>
                <BotonEdadCheck idJugador={jugadorLiga.idJugador} checkInicial={jugadorLiga.anoCheck} enLista={true} />
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'categoria') {
          return (
            <TableCellCategoria key={col.field} estadoJugador={estadoCategoriaJugador} sx={{ textAlign: 'center' }}>
              {categoriaJugador}
            </TableCellCategoria>
          );
        }

        if (col.field === 'goles') {
          return (
            <TypographyTableApp key={col.field} sx={{ borderLeft: '1px solid #e2e8f0', textAlign: 'center' }}>
              {jugadorLiga.goles}
            </TypographyTableApp>
          );
        }

        return (
          <TypographyTableApp key={col.field} sx={{ textAlign: 'center' }}>
            {jugadorLiga[col.field]}
          </TypographyTableApp>
        );
      })}
    </TableRow>
  );
};

/**
 * Tabla de jugadores de una liga.
 */
const ListaJugadores = ({ nombreLista, datosLiga, jugadores, onUpdate, loading = false }) => {
  const { obtenerListasConJugador } = useContext(Context);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState({ field: 'convocatorias', direction: 'desc' });
  const [listasCache, setListasCache] = useState({});

  // Eliminar jugadores duplicados
  const jugadoresDeduplicados = useMemo(() => {
    if (!Array.isArray(jugadores)) {
      return [];
    }
    const idsJugadores = new Set();
    return jugadores.filter((j) => {
      if (idsJugadores.has(j.idJugador)) {
        return false;
      }
      idsJugadores.add(j.idJugador);
      return true;
    });
  }, [jugadores]);

  const jugadoresOrdenados = useMemo(() => {
    return ordenarLista(jugadoresDeduplicados, sort);
  }, [jugadoresDeduplicados, sort]);

  useEffect(() => {
    async function cargarEstadoListas() {
      if (!Array.isArray(jugadoresOrdenados)) {
        return;
      }
      // Cargar estado de listas para los jugadores de la página visible
      const jugadoresVista = jugadoresOrdenados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
      const promesas = jugadoresVista.map(async (j) => {
        if (listasCache[j.idJugador] !== undefined) {
          return null;
        }
        const listas = await obtenerListasConJugador(j.idJugador);
        return { id: j.idJugador, listas: listas || [] };
      });
      const resultados = (await Promise.all(promesas)).filter((r) => r !== null);
      if (resultados.length > 0) {
        setListasCache((prev) => {
          const nuevoCache = { ...prev };
          resultados.forEach((r) => (nuevoCache[r.id] = r.listas));
          return nuevoCache;
        });
      }
    }
    cargarEstadoListas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jugadoresOrdenados, page, rowsPerPage]);

  const handleListasChange = (idJugador, listas) => {
    setListasCache((prev) => ({ ...prev, [idJugador]: listas || [] }));
  };

  if (!nombreLista) {
    return null;
  }

  if (!datosLiga && !loading) {
    return null;
  }

  if (!Array.isArray(jugadores) && !loading) {
    return null;
  }

  return (
    <TableModuleApp
      title={nombreLista}
      columns={columns}
      sort={sort}
      setSort={setSort}
      loading={loading}
      data={jugadoresOrdenados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
      renderRow={(jugador) => (
        <FilaJugadorLiga
          key={jugador.idJugador}
          datosLiga={datosLiga}
          jugadorLiga={jugador}
          columns={columns}
          listasJugador={listasCache[jugador.idJugador] || []}
          handleListasChange={(listas) => handleListasChange(jugador.idJugador, listas)}
          onUpdate={onUpdate}
        />
      )}
      hasPagination
      page={page}
      setPage={setPage}
      rowsPerPage={rowsPerPage}
      setRowsPerPage={setRowsPerPage}
      totalCount={jugadoresOrdenados.length}
    />
  );
};

ListaJugadores.propTypes = {
  nombreLista: PropTypes.string,
  datosLiga: PropTypes.object,
  jugadores: PropTypes.array,
  onUpdate: PropTypes.func,
  loading: PropTypes.bool,
};

FilaJugadorLiga.propTypes = {
  datosLiga: PropTypes.object,
  jugadorLiga: PropTypes.object,
  columns: PropTypes.array,
  listasJugador: PropTypes.array,
  handleListasChange: PropTypes.func,
  onUpdate: PropTypes.func,
};

export default ListaJugadores;
